import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/** Emitted saat backend return 401 → AuthContext dengar event ini untuk clear token + redirect.
 *  Dispatch via window agar tidak coupling ke React hook. */
export const AUTH_LOGOUT_EVENT = "hris-auth-401";

/** Axios instance dengan:
 *  - Timeout 30 detik (cukup untuk request berat: laporan/export)
 *  - Bearer token dari localStorage
 *  - Response interceptor: hanya 401 yang trigger logout event
 *  - Retry 1x otomatis untuk network error / timeout / 5xx (transient) */
export const api = axios.create({
  baseURL: API,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("hris_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Mark for retry logic
  if (config._retryCount === undefined) config._retryCount = 0;
  return config;
});

/** Kategori error yang boleh di-retry (transient issue, bukan authz/bad request). */
function isRetryable(error) {
  // Network error (no response) OR timeout OR server error 5xx
  if (!error.response) return true;                     // network / timeout / CORS
  const status = error.response.status;
  return status >= 500 && status < 600;                  // 500-599 transient server
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config || {};
    const status = error.response?.status;

    // 401 → session invalid: clear token + notify AuthContext untuk redirect ke /login.
    // TIDAK dilakukan untuk error jaringan/timeout — user tetap login, aksi bisa diretry.
    if (status === 401) {
      localStorage.removeItem("hris_token");
      window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT, { detail: { reason: "unauthorized" } }));
      return Promise.reject(error);
    }

    // Retry sekali untuk transient error (network / timeout / 5xx).
    // Jangan retry POST/PUT/PATCH/DELETE yang bukan idempotent — bisa menyebabkan double-submit.
    const method = (config.method || "get").toLowerCase();
    const idempotent = ["get", "head", "options"].includes(method);
    if (idempotent && isRetryable(error) && (config._retryCount || 0) < 1) {
      config._retryCount = (config._retryCount || 0) + 1;
      // small backoff
      await new Promise((res) => setTimeout(res, 400));
      return api(config);
    }

    return Promise.reject(error);
  }
);

/** Optional: panggil `refreshToken()` untuk perpanjang session tanpa re-login.
 *  AuthContext memanggil ini secara periodik (~setiap 6 jam) saat user aktif. */
export async function refreshToken() {
  const token = localStorage.getItem("hris_token");
  if (!token) return null;
  try {
    const { data } = await api.post("/auth/refresh");
    if (data?.token) {
      localStorage.setItem("hris_token", data.token);
      return data.token;
    }
  } catch (_err) {
    // silent fail — kalau 401, response interceptor sudah handle logout
  }
  return null;
}

export const idr = (n) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n || 0);

export const fmtNum = (n) => new Intl.NumberFormat("id-ID").format(n || 0);

/** Short currency for tight spaces / mobile podium cards. Rp 48,3jt / Rp 500rb / Rp 750 */
export const idrShort = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000_000) return "Rp " + (v / 1_000_000_000).toFixed(1).replace(".0", "") + "M";
  if (v >= 1_000_000) return "Rp " + (v / 1_000_000).toFixed(1).replace(".0", "") + "jt";
  if (v >= 1_000) return "Rp " + Math.round(v / 1_000) + "rb";
  return "Rp " + Math.round(v);
};

/**
 * Pesan galat yang AMAN dirender React.
 *
 * Ditemukan 11 Sep 2026 saat verifikasi browser: `toast.error(e?.response?.data?.detail)`
 * MENGHANCURKAN halaman kalau backend membalas **422** — FastAPI mengirim
 * `detail` sebagai ARRAY OBJEK (`{type, loc, msg, input, ctx}`), dan React
 * menolak merendernya ("Objects are not valid as a React child"), sehingga
 * seluruh pohon komponen unmount → layar putih. Terbukti di halaman Login
 * dengan email berdomain khusus (`@sesuatu.local`) yang lolos validasi
 * `type="email"` browser tapi ditolak `EmailStr` di server.
 *
 * Pola `.data?.detail` dipakai 559 kali di 119 berkas, jadi ini bukan masalah
 * satu halaman. Helper ini yang dipakai di tempat yang sudah terbukti kena dan
 * di halaman baru; penyisiran sisanya pekerjaan tersendiri.
 */
export function pesanGalat(err, baku = "Terjadi kesalahan") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d)) {
    const pesan = d
      .map((x) => (typeof x === "string" ? x : x?.msg))
      .filter(Boolean)
      .join(", ");
    if (pesan) return pesan;
  }
  if (d && typeof d === "object" && typeof d.msg === "string") return d.msg;
  return baku;
}
